import { Link, useLocation } from "react-router-dom";
import { useEffect } from "react";
import classNames from "classnames";
import { IconX } from "@tabler/icons-react";

import type { AppRoute } from "../../../../components/dashboard/authenticated_app";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { APP_NAME } from "../../../../common/constants/app";
import logo from "../../../../assets/images/logo.svg";
import { Text } from "../../../../components/common/typography";
import { UserAvatar } from "../../../../components/common/app-shell/user-avatar";

type SidebarProps = {
  navigation: AppRoute[];
  open: boolean;
  setOpen: (open: boolean) => void;
  username: string;
  userId: string;
};

const DEFAULT_SECTION = "Main";

function groupNavBySection(items: AppRoute[]) {
  const groups = new Map<string, AppRoute[]>();
  for (const item of items) {
    if (item.hideInNavigation) continue;
    const section = item.section ?? DEFAULT_SECTION;
    if (!groups.has(section)) groups.set(section, []);
    groups.get(section)!.push(item);
  }
  return Array.from(groups.entries());
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function NavItem({
  item,
  isActive,
}: {
  item: AppRoute;
  isActive: boolean;
}) {
  return (
    <Link
      to={item.path}
      className={classNames(
        "flex gap-2 items-center p-3 w-full transition-colors",
        isActive
          ? "bg-bgWhite rounded-[10px] shadow-[0_1px_3px_0_rgba(0,0,0,0.1),0_1px_2px_0_rgba(0,0,0,0.1)]"
          : "rounded-md hover:bg-bgMuted"
      )}
    >
      <span
        className={classNames(
          "shrink-0 size-5 flex items-center justify-center",
          isActive ? "text-textDark" : "text-textBody"
        )}
      >
        {item.icon(isActive)}
      </span>
      <Text
        variant="bodySm"
        tone="inherit"
        className={classNames(
          "whitespace-nowrap",
          isActive ? "text-textDark" : "text-textBody"
        )}
      >
        {item.title}
      </Text>
    </Link>
  );
}

function SidebarContent({
  navigation,
  fallbackName,
}: {
  navigation: AppRoute[];
  fallbackName: string;
}) {
  const location = useLocation();
  const sections = groupNavBySection(navigation);
  const displayName = fallbackName || "";
  const initials = getInitials(displayName) || "U";

  return (
    <div className="flex flex-col h-full bg-bgLight border border-borderDefault rounded-r-[12px]">
      <div className="flex items-center p-6 w-full shrink-0">
        <Link
          to={AppRoutes.INDEX}
          className="flex items-center gap-2"
          aria-label={APP_NAME}
        >
          <img src={logo} alt={APP_NAME} className="h-[37px] w-auto" />
        </Link>
      </div>

      {/* Scrollable nav: absorbs the space between the logo header and the
          pinned user footer so long menus don't push the footer offscreen.
          `min-h-0` is required for a flex child to actually overflow
          (without it the child grows to fit its content and never scrolls).
          The themed scrollbar is global from index.css. */}
      <nav className="flex flex-1 flex-col px-3 w-full min-h-0 overflow-y-auto">
        {sections.map(([section, items]) => (
          <div key={section} className="flex flex-col w-full">
            <div className="flex h-8 items-center pl-3 text-accentCyan700">
              <Text variant="capsSm" tone="inherit">
                {section}
              </Text>
            </div>
            {items.map((item) => (
              <NavItem
                key={item.path}
                item={item}
                isActive={
                  location.pathname.toLowerCase() === item.path.toLowerCase()
                }
              />
            ))}
          </div>
        ))}
      </nav>

      <div className="flex items-center gap-3 px-5 py-4 border-t border-borderDefault rounded-br-[12px] w-full shrink-0">
        <UserAvatar initials={initials} size="md" className="shrink-0" />
        <div className="flex flex-col gap-0.5 min-w-0 flex-1">
          <Text
            variant="bodySmBold"
            tone="inherit"
            className="text-textDark truncate"
          >
            {displayName}
          </Text>
          <div className="flex items-center gap-1.5">
            <span className="size-2 rounded-full bg-accentGreenDark" />
            <Text variant="bodyXs" tone="greenDark">Active</Text>
          </div>
        </div>
      </div>
    </div>
  );
}

export function Sidebar({
  navigation,
  open,
  setOpen,
  username,
}: SidebarProps) {
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location, setOpen]);

  return (
    <>
      {/* Mobile drawer */}
      <div
        className={classNames(
          "fixed inset-0 z-40 lg:hidden transition-opacity print:hidden",
          open ? "opacity-100" : "opacity-0 pointer-events-none"
        )}
      >
        <div
          className="absolute inset-0 bg-black/40"
          onClick={() => setOpen(false)}
        />
        <div
          className={classNames(
            "absolute inset-y-0 left-0 w-[280px] transition-transform",
            open ? "translate-x-0" : "-translate-x-full"
          )}
        >
          <div className="relative h-full">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="absolute top-3 right-3 z-10 p-2 text-textBody hover:text-textDark"
              aria-label="Close sidebar"
            >
              <IconX size={20} />
            </button>
            <SidebarContent
              navigation={navigation}
              fallbackName={username}
            />
          </div>
        </div>
      </div>

      {/* Desktop */}
      <aside className="hidden lg:block w-[260px] shrink-0 h-full print:hidden">
        <SidebarContent
          navigation={navigation}
          fallbackName={username}
        />
      </aside>
    </>
  );
}
